import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShelfValidation } from '../../hooks/useShelfValidation';
import { useClickOutside } from '../../hooks/useClickOutside';

interface ShelfAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (shelfName: string) => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}

function normalizeForSearch(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '');
}

function matchesFuzzy(query: string, shelf: string): boolean {
  const normalizedQuery = normalizeForSearch(query);
  const normalizedShelf = normalizeForSearch(shelf);
  return normalizedShelf.includes(normalizedQuery);
}

function ShelfAutocompleteInput({
  value,
  onChange,
  onSelect,
  placeholder = 'Type shelf name',
  className = 'pbl-sku-input',
  inputRef: externalInputRef,
  disabled = false,
}: ShelfAutocompleteInputProps): React.ReactElement {
  const { allShelfNames, loading: shelvesLoading } = useShelfValidation();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);

  const filteredShelves = useMemo(() => {
    if (!value.trim() || shelvesLoading) return [];
    return allShelfNames
      .filter((shelf) => matchesFuzzy(value, shelf))
      .slice(0, 10);
  }, [value, allShelfNames, shelvesLoading]);

  useEffect(() => {
    if (skipAutocompleteOpenRef.current) {
      skipAutocompleteOpenRef.current = false;
      setSelectedIndex(-1);
      setShowAutocomplete(false);
      return;
    }
    if (value.trim() && filteredShelves.length > 0) {
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
    setSelectedIndex(-1);
  }, [value, filteredShelves.length]);

  useEffect(() => {
    if (showAutocomplete && inputRef.current && autocompleteRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, value, filteredShelves, inputRef]);

  useClickOutside(
    [autocompleteRef, inputRef],
    () => setShowAutocomplete(false),
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value.toUpperCase());
    },
    [onChange]
  );

  const handleSelectShelf = useCallback(
    (shelf: string) => {
      skipAutocompleteOpenRef.current = true;
      setShowAutocomplete(false);
      setSelectedIndex(-1);
      onSelect(shelf);
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showAutocomplete || filteredShelves.length === 0) {
        if (e.key === 'Escape') {
          setShowAutocomplete(false);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredShelves.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleSelectShelf(filteredShelves[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowAutocomplete(false);
      }
    },
    [showAutocomplete, filteredShelves, selectedIndex, handleSelectShelf]
  );

  return (
    <div className="pbl-sku-input-wrapper">
      <input
        ref={inputRef as React.Ref<HTMLInputElement>}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        style={{ textTransform: 'uppercase' }}
        disabled={disabled}
      />
      {showAutocomplete && filteredShelves.length > 0 && (
        <div ref={autocompleteRef} className="pbl-autocomplete-dropdown">
          {filteredShelves.map((shelf, index) => (
            <div
              key={shelf}
              className={`pbl-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelectShelf(shelf)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {shelf}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ShelfAutocompleteInput;
